import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  AlertTriangle,
  Archive,
  ArrowDownToLine,
  ArrowUpFromLine,
  Barcode,
  Boxes,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Clock3,
  Download,
  FileSpreadsheet,
  History,
  Loader2,
  Package,
  PackageCheck,
  PackageMinus,
  PackagePlus,
  RefreshCw,
  RotateCcw,
  Search,
  Shirt,
  Tag,
  Trash2,
  Upload,
  UserRound,
  X,
} from "lucide-react";

import { api } from "../../api";

const DEFAULT_CATEGORIES = [
  "Uniform",
  "Sports equipment",
  "First aid equipment",
  "Adventure training equipment",
  "DofE equipment",
  "Fieldcraft equipment",
  "Event equipment",
  "Archery equipment",
  "Band equipment",
  "Other",
];

const TABS = [
  {
    key: "stock",
    label: "Stock",
    icon: Boxes,
  },
  {
    key: "transactions",
    label: "Scan / Issue",
    icon: Barcode,
  },
  {
    key: "loans",
    label: "Loans",
    icon: UserRound,
  },
  {
    key: "history",
    label: "History",
    icon: History,
  },
  {
    key: "import",
    label: "Import",
    icon: Upload,
  },
];

const TRANSACTION_MODES = {
  add: {
    label: "Add Stock",
    shortLabel: "Add",
    icon: PackagePlus,
    explanation:
      "Increase stock on hand. If the code is new, create the item at the same time.",
    buttonClass:
      "bg-emerald-700 text-white",
  },
  lend: {
    label: "Issue / Lend",
    shortLabel: "Issue",
    icon: ArrowUpFromLine,
    explanation:
      "Issue equipment or uniform on a returnable basis. Total stock stays the same; available stock reduces.",
    buttonClass:
      "bg-raf-navy text-white",
  },
  return: {
    label: "Return",
    shortLabel: "Return",
    icon: ArrowDownToLine,
    explanation:
      "Return previously issued equipment to available stock.",
    buttonClass:
      "bg-blue-700 text-white",
  },
  remove: {
    label: "Remove Stock",
    shortLabel: "Remove",
    icon: PackageMinus,
    explanation:
      "Permanently reduce total stock, for example disposal, consumption, write-off or transfer.",
    buttonClass:
      "bg-red-700 text-white",
  },
};

function formatDate(value) {
  if (!value) {
    return "—";
  }

  try {
    return new Intl.DateTimeFormat(
      "en-GB",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }
    ).format(new Date(value));
  } catch {
    return value;
  }
}

function formatDateOnly(value) {
  if (!value) {
    return "No due date";
  }

  const date =
    new Date(
      `${value}T12:00:00`
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return value;
  }

  return new Intl.DateTimeFormat(
    "en-GB",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  ).format(date);
}

function isOverdue(loan) {
  if (
    !loan?.due_date ||
    loan?.status !== "open"
  ) {
    return false;
  }

  const due =
    new Date(
      `${loan.due_date}T23:59:59`
    );

  return (
    !Number.isNaN(
      due.getTime()
    ) &&
    due.getTime() <
      Date.now()
  );
}

function outstandingQuantity(
  loan
) {
  return Math.max(
    0,
    Number(
      loan?.quantity || 0
    ) -
      Number(
        loan?.returned_quantity ||
          0
      )
  );
}

function actionLabel(action) {
  const labels = {
    create: "Item created",
    scan_in: "Stock added",
    scan_in_new:
      "New item scanned in",
    import_create:
      "Imported new item",
    import_update:
      "Imported stock added",
    scan_out:
      "Stock removed",
    lend_out:
      "Issued / lent",
    return: "Returned",
  };

  return (
    labels[action] ||
    action ||
    "Inventory action"
  );
}

function actionTone(action) {
  if (
    [
      "create",
      "scan_in",
      "scan_in_new",
      "import_create",
      "import_update",
      "return",
    ].includes(action)
  ) {
    return "green";
  }

  if (
    action === "lend_out"
  ) {
    return "blue";
  }

  if (
    action === "scan_out"
  ) {
    return "red";
  }

  return "slate";
}

function ToneBadge({
  tone = "slate",
  children,
}) {
  const classes = {
    slate:
      "bg-slate-100 text-slate-700 border-slate-200",
    green:
      "bg-emerald-100 text-emerald-800 border-emerald-200",
    blue:
      "bg-blue-100 text-blue-800 border-blue-200",
    amber:
      "bg-amber-100 text-amber-900 border-amber-200",
    red:
      "bg-red-100 text-red-800 border-red-200",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold ${classes[tone]}`}
    >
      {children}
    </span>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  note,
  tone = "navy",
}) {
  const iconClasses = {
    navy:
      "bg-blue-50 text-raf-navy",
    green:
      "bg-emerald-50 text-emerald-700",
    amber:
      "bg-amber-50 text-amber-700",
    red:
      "bg-red-50 text-red-700",
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconClasses[tone]}`}
        >
          <Icon size={20} />
        </div>

        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">
            {label}
          </p>

          <p className="text-2xl font-black text-raf-navy mt-1">
            {value}
          </p>

          {note && (
            <p className="text-xs text-slate-500 mt-1">
              {note}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Modal({
  title,
  eyebrow,
  onClose,
  children,
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl">
        <div className="sticky top-0 z-10 bg-raf-navy text-white px-5 py-4 flex items-start justify-between gap-4 border-b-4 border-raf-red">
          <div>
            {eyebrow && (
              <p className="text-xs uppercase tracking-widest text-white/60 font-bold">
                {eyebrow}
              </p>
            )}

            <h2 className="text-xl font-black mt-1">
              {title}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10"
            aria-label="Close"
          >
            <X size={21} />
          </button>
        </div>

        {children}
      </div>
    </div>
  );
}

function NewItemModal({
  categories,
  onClose,
  onCreated,
}) {
  const [form, setForm] =
    useState({
      qr_code: "",
      name: "",
      category:
        categories[0] ||
        "Other",
      size: "",
      quantity: 1,
      notes: "",
    });

  const [busy, setBusy] =
    useState(false);

  const [error, setError] =
    useState("");

  const update = (
    key,
    value
  ) =>
    setForm(
      (previous) => ({
        ...previous,
        [key]: value,
      })
    );

  const submit = async (
    event
  ) => {
    event.preventDefault();

    setError("");

    if (
      !form.qr_code.trim()
    ) {
      setError(
        "Enter or scan a QR / asset code."
      );
      return;
    }

    if (!form.name.trim()) {
      setError(
        "Enter an item name."
      );
      return;
    }

    setBusy(true);

    try {
      const { data } =
        await api.post(
          "/inventory/items",
          {
            ...form,
            qr_code:
              form.qr_code.trim(),
            name:
              form.name.trim(),
            quantity:
              Math.max(
                1,
                Number(
                  form.quantity ||
                    1
                )
              ),
          }
        );

      await onCreated(data);
    } catch (err) {
      setError(
        err?.response?.data
          ?.detail ||
          "Unable to create the inventory item."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Create Inventory Item"
      eyebrow="Stores"
      onClose={onClose}
    >
      <form
        onSubmit={submit}
        className="p-5 md:p-6 space-y-4"
      >
        <div className="rounded-xl bg-blue-50 border border-blue-200 p-4">
          <p className="text-sm text-blue-950">
            Each inventory line
            needs a unique QR or
            asset code. A USB QR /
            barcode scanner can be
            used here because it
            behaves like a keyboard.
          </p>
        </div>

        <label className="block">
          <span className="text-sm font-bold text-raf-navy">
            QR / asset code
          </span>

          <div className="relative mt-2">
            <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />

            <input
              autoFocus
              value={
                form.qr_code
              }
              onChange={(
                event
              ) =>
                update(
                  "qr_code",
                  event.target.value
                )
              }
              placeholder="e.g. 1471-UNI-001"
              className="w-full border border-slate-300 rounded-xl pl-11 pr-4 py-3 outline-none focus:border-raf-blue"
            />
          </div>
        </label>

        <label className="block">
          <span className="text-sm font-bold text-raf-navy">
            Item name
          </span>

          <input
            value={form.name}
            onChange={(
              event
            ) =>
              update(
                "name",
                event.target.value
              )
            }
            placeholder="e.g. No 2 Blue Shirt"
            className="mt-2 w-full border border-slate-300 rounded-xl px-4 py-3 outline-none focus:border-raf-blue"
          />
        </label>

        <div className="grid sm:grid-cols-3 gap-3">
          <label>
            <span className="text-sm font-bold text-raf-navy">
              Category
            </span>

            <select
              value={
                form.category
              }
              onChange={(
                event
              ) =>
                update(
                  "category",
                  event.target.value
                )
              }
              className="mt-2 w-full border border-slate-300 rounded-xl px-3 py-3 bg-white"
            >
              {categories.map(
                (category) => (
                  <option
                    key={
                      category
                    }
                    value={
                      category
                    }
                  >
                    {category}
                  </option>
                )
              )}
            </select>
          </label>

          <label>
            <span className="text-sm font-bold text-raf-navy">
              Size / variant
            </span>

            <input
              value={form.size}
              onChange={(
                event
              ) =>
                update(
                  "size",
                  event.target.value
                )
              }
              placeholder="Optional"
              className="mt-2 w-full border border-slate-300 rounded-xl px-3 py-3"
            />
          </label>

          <label>
            <span className="text-sm font-bold text-raf-navy">
              Quantity
            </span>

            <input
              type="number"
              min="1"
              value={
                form.quantity
              }
              onChange={(
                event
              ) =>
                update(
                  "quantity",
                  event.target.value
                )
              }
              className="mt-2 w-full border border-slate-300 rounded-xl px-3 py-3"
            />
          </label>
        </div>

        <label className="block">
          <span className="text-sm font-bold text-raf-navy">
            Notes
          </span>

          <textarea
            rows={3}
            value={
              form.notes
            }
            onChange={(
              event
            ) =>
              update(
                "notes",
                event.target.value
              )
            }
            placeholder="Optional storage, condition or other notes"
            className="mt-2 w-full border border-slate-300 rounded-xl px-4 py-3 resize-y"
          />
        </label>

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 p-3 flex gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />

            <p className="text-sm text-red-800">
              {error}
            </p>
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="border border-slate-300 rounded-lg px-4 py-2.5 font-semibold"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-raf-navy text-white px-4 py-2.5 font-bold disabled:opacity-50"
          >
            {busy ? (
              <Loader2
                size={18}
                className="animate-spin"
              />
            ) : (
              <PackagePlus
                size={18}
              />
            )}

            Create Item
          </button>
        </div>
      </form>
    </Modal>
  );
}

function TransactionPanel({
  items,
  categories,
  loans,
  initialMode = "lend",
  initialCode = "",
  initialLoan = null,
  onComplete,
}) {
  const [mode, setMode] =
    useState(initialMode);

  const [form, setForm] =
    useState({
      qr_code:
        initialCode || "",
      quantity:
        initialLoan
          ? outstandingQuantity(
              initialLoan
            )
          : 1,
      name: "",
      category:
        categories[0] ||
        "Other",
      size: "",
      notes: "",
      lent_to_name:
        initialLoan
          ?.lent_to_name ||
        "",
      lent_to_contact:
        "",
      due_date: "",
      loan_id:
        initialLoan?.id ||
        "",
    });

  const [busy, setBusy] =
    useState(false);

  const [error, setError] =
    useState("");

  const [success, setSuccess] =
    useState("");

  const codeInputRef =
    useRef(null);

  useEffect(() => {
    setMode(initialMode);

    setForm(
      (previous) => ({
        ...previous,
        qr_code:
          initialCode ||
          previous.qr_code,
        quantity:
          initialLoan
            ? outstandingQuantity(
                initialLoan
              )
            : previous.quantity,
        loan_id:
          initialLoan?.id ||
          "",
        lent_to_name:
          initialLoan
            ?.lent_to_name ||
          previous.lent_to_name,
      })
    );

    window.setTimeout(
      () =>
        codeInputRef.current?.focus(),
      50
    );
  }, [
    initialCode,
    initialLoan,
    initialMode,
  ]);

  const matchingItem =
    useMemo(
      () =>
        items.find(
          (item) =>
            String(
              item.qr_code
            ).trim() ===
            String(
              form.qr_code
            ).trim()
        ) || null,
      [
        form.qr_code,
        items,
      ]
    );

  const matchingLoans =
    useMemo(
      () =>
        loans.filter(
          (loan) =>
            loan.status ===
              "open" &&
            String(
              loan.qr_code
            ).trim() ===
              String(
                form.qr_code
              ).trim()
        ),
      [
        form.qr_code,
        loans,
      ]
    );

  const update = (
    key,
    value
  ) =>
    setForm(
      (previous) => ({
        ...previous,
        [key]: value,
      })
    );

  const changeMode = (
    nextMode
  ) => {
    setMode(nextMode);
    setError("");
    setSuccess("");

    setForm(
      (previous) => ({
        ...previous,
        loan_id:
          nextMode ===
          "return"
            ? previous.loan_id
            : "",
      })
    );

    window.setTimeout(
      () =>
        codeInputRef.current?.focus(),
      50
    );
  };

  const submit = async (
    event
  ) => {
    event.preventDefault();

    setError("");
    setSuccess("");

    const qrCode =
      form.qr_code.trim();

    const quantity =
      Math.max(
        1,
        Number(
          form.quantity || 1
        )
      );

    if (!qrCode) {
      setError(
        "Scan or enter a QR / asset code."
      );
      return;
    }

    if (
      mode !== "add" &&
      !matchingItem
    ) {
      setError(
        "No inventory item exists for that code."
      );
      return;
    }

    if (
      mode === "lend" &&
      !form.lent_to_name.trim()
    ) {
      setError(
        "Enter who the item is issued to. For cadets, use the service number where practical."
      );
      return;
    }

    if (
      mode === "return" &&
      matchingLoans.length ===
        0 &&
      !form.loan_id
    ) {
      setError(
        "No open loan was found for this code."
      );
      return;
    }

    if (
      mode === "add" &&
      !matchingItem &&
      !form.name.trim()
    ) {
      setError(
        "This is a new code, so enter an item name."
      );
      return;
    }

    setBusy(true);

    try {
      let response;

      if (
        mode === "add"
      ) {
        response =
          await api.post(
            "/inventory/scan-in",
            {
              qr_code:
                qrCode,
              quantity,
              name:
                matchingItem
                  ? ""
                  : form.name.trim(),
              category:
                matchingItem
                  ? matchingItem.category
                  : form.category,
              size:
                matchingItem
                  ? form.size
                  : form.size,
              notes:
                form.notes.trim(),
            }
          );
      }

      if (
        mode === "remove"
      ) {
        response =
          await api.post(
            "/inventory/scan-out",
            {
              qr_code:
                qrCode,
              quantity,
              notes:
                form.notes.trim(),
            }
          );
      }

      if (
        mode === "lend"
      ) {
        response =
          await api.post(
            "/inventory/lend-out",
            {
              qr_code:
                qrCode,
              quantity,
              lent_to_name:
                form.lent_to_name.trim(),
              lent_to_contact:
                form.lent_to_contact.trim(),
              due_date:
                form.due_date,
              notes:
                form.notes.trim(),
            }
          );
      }

      if (
        mode === "return"
      ) {
        response =
          await api.post(
            "/inventory/scan-return",
            {
              qr_code:
                qrCode,
              quantity,
              loan_id:
                form.loan_id ||
                "",
              notes:
                form.notes.trim(),
            }
          );
      }

      const messages = {
        add: matchingItem
          ? "Stock added successfully."
          : "New inventory item created and stock added.",
        remove:
          "Stock removed successfully.",
        lend:
          "Item issued successfully.",
        return:
          "Return recorded successfully.",
      };

      setSuccess(
        messages[mode]
      );

      await onComplete(
        response?.data
      );

      if (
        mode === "lend" ||
        mode === "return"
      ) {
        setForm(
          (previous) => ({
            ...previous,
            qr_code: "",
            quantity: 1,
            lent_to_name:
              "",
            lent_to_contact:
              "",
            due_date: "",
            loan_id: "",
            notes: "",
          })
        );
      } else {
        setForm(
          (previous) => ({
            ...previous,
            quantity: 1,
            notes: "",
          })
        );
      }

      window.setTimeout(
        () =>
          codeInputRef.current?.focus(),
        50
      );
    } catch (err) {
      setError(
        err?.response?.data
          ?.detail ||
          "Unable to complete the inventory transaction."
      );
    } finally {
      setBusy(false);
    }
  };

  const ModeIcon =
    TRANSACTION_MODES[
      mode
    ].icon;

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-2">
        {Object.entries(
          TRANSACTION_MODES
        ).map(
          ([
            key,
            definition,
          ]) => {
            const Icon =
              definition.icon;

            const active =
              mode === key;

            return (
              <button
                key={key}
                type="button"
                onClick={() =>
                  changeMode(
                    key
                  )
                }
                className={[
                  "rounded-xl border p-4 text-left transition",
                  active
                    ? "border-raf-blue bg-blue-50 shadow-sm"
                    : "border-slate-200 bg-white hover:border-slate-300",
                ].join(" ")}
              >
                <Icon
                  size={21}
                  className={
                    active
                      ? "text-raf-blue"
                      : "text-slate-500"
                  }
                />

                <p className="font-black text-raf-navy mt-2">
                  {
                    definition.label
                  }
                </p>

                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  {
                    definition.explanation
                  }
                </p>
              </button>
            );
          }
        )}
      </div>

      <form
        onSubmit={submit}
        className="bg-white border border-slate-200 rounded-xl overflow-hidden"
      >
        <div className="bg-raf-navy text-white px-5 py-4 flex items-center gap-3">
          <ModeIcon
            size={22}
          />

          <div>
            <p className="text-xs uppercase tracking-widest text-white/60 font-bold">
              Stock transaction
            </p>

            <h3 className="font-black text-xl">
              {
                TRANSACTION_MODES[
                  mode
                ].label
              }
            </h3>
          </div>
        </div>

        <div className="p-5 md:p-6 space-y-4">
          <div>
            <label className="text-sm font-bold text-raf-navy">
              Scan QR / asset
              code
            </label>

            <div className="relative mt-2">
              <Barcode className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-400" />

              <input
                ref={
                  codeInputRef
                }
                value={
                  form.qr_code
                }
                onChange={(
                  event
                ) => {
                  update(
                    "qr_code",
                    event.target.value
                  );

                  setError("");
                  setSuccess("");
                }}
                placeholder="Scan or type code"
                autoComplete="off"
                className="w-full border-2 border-slate-300 rounded-xl pl-13 pr-4 py-4 text-xl font-black tracking-wide outline-none focus:border-raf-blue"
                style={{
                  paddingLeft:
                    "3.25rem",
                }}
              />
            </div>

            <p className="text-xs text-slate-500 mt-2">
              USB barcode / QR
              scanners normally
              work without any
              special browser
              permissions.
            </p>
          </div>

          {form.qr_code &&
            matchingItem && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-emerald-700 font-bold">
                      Item found
                    </p>

                    <p className="font-black text-raf-navy text-lg mt-1">
                      {
                        matchingItem.name
                      }
                    </p>

                    <p className="text-sm text-slate-600 mt-1">
                      {
                        matchingItem.category
                      }
                      {matchingItem.size
                        ? ` • ${matchingItem.size}`
                        : ""}
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <ToneBadge tone="green">
                      {
                        matchingItem.quantity_available
                      }{" "}
                      available
                    </ToneBadge>

                    <ToneBadge>
                      {
                        matchingItem.quantity_total
                      }{" "}
                      total
                    </ToneBadge>
                  </div>
                </div>
              </div>
            )}

          {form.qr_code &&
            !matchingItem &&
            mode !== "add" && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />

                <p className="text-sm text-red-800">
                  No inventory item
                  currently uses
                  this code.
                </p>
              </div>
            )}

          {mode === "add" &&
            form.qr_code &&
            !matchingItem && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                <p className="font-bold text-blue-950">
                  New code detected
                </p>

                <p className="text-sm text-blue-900 mt-1">
                  Enter the item
                  details below and
                  it will be created
                  automatically.
                </p>

                <div className="grid sm:grid-cols-3 gap-3 mt-4">
                  <label className="sm:col-span-3">
                    <span className="text-sm font-bold text-raf-navy">
                      Item name
                    </span>

                    <input
                      value={
                        form.name
                      }
                      onChange={(
                        event
                      ) =>
                        update(
                          "name",
                          event.target.value
                        )
                      }
                      className="mt-2 w-full border border-slate-300 rounded-lg px-3 py-2.5"
                    />
                  </label>

                  <label>
                    <span className="text-sm font-bold text-raf-navy">
                      Category
                    </span>

                    <select
                      value={
                        form.category
                      }
                      onChange={(
                        event
                      ) =>
                        update(
                          "category",
                          event.target.value
                        )
                      }
                      className="mt-2 w-full border border-slate-300 bg-white rounded-lg px-3 py-2.5"
                    >
                      {categories.map(
                        (
                          category
                        ) => (
                          <option
                            key={
                              category
                            }
                            value={
                              category
                            }
                          >
                            {
                              category
                            }
                          </option>
                        )
                      )}
                    </select>
                  </label>

                  <label>
                    <span className="text-sm font-bold text-raf-navy">
                      Size /
                      variant
                    </span>

                    <input
                      value={
                        form.size
                      }
                      onChange={(
                        event
                      ) =>
                        update(
                          "size",
                          event.target.value
                        )
                      }
                      className="mt-2 w-full border border-slate-300 rounded-lg px-3 py-2.5"
                    />
                  </label>
                </div>
              </div>
            )}

          <div className="grid sm:grid-cols-2 gap-3">
            <label>
              <span className="text-sm font-bold text-raf-navy">
                Quantity
              </span>

              <input
                type="number"
                min="1"
                value={
                  form.quantity
                }
                onChange={(
                  event
                ) =>
                  update(
                    "quantity",
                    event.target.value
                  )
                }
                className="mt-2 w-full border border-slate-300 rounded-xl px-4 py-3"
              />
            </label>

            {mode === "add" &&
              matchingItem && (
                <label>
                  <span className="text-sm font-bold text-raf-navy">
                    Size /
                    variant
                  </span>

                  <input
                    value={
                      form.size
                    }
                    onChange={(
                      event
                    ) =>
                      update(
                        "size",
                        event.target.value
                      )
                    }
                    placeholder={
                      matchingItem.size ||
                      "Leave blank to keep current"
                    }
                    className="mt-2 w-full border border-slate-300 rounded-xl px-4 py-3"
                  />
                </label>
              )}
          </div>

          {mode === "lend" && (
            <div className="rounded-xl border border-slate-200 p-4 space-y-3">
              <div>
                <p className="font-black text-raf-navy">
                  Issue details
                </p>

                <p className="text-xs text-slate-500 mt-1">
                  For cadets, using
                  their service
                  number avoids
                  storing their name
                  unnecessarily.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <label>
                  <span className="text-sm font-bold text-raf-navy">
                    Issued to /
                    reference
                  </span>

                  <input
                    value={
                      form.lent_to_name
                    }
                    onChange={(
                      event
                    ) =>
                      update(
                        "lent_to_name",
                        event.target.value
                      )
                    }
                    placeholder="e.g. 3014710026001"
                    className="mt-2 w-full border border-slate-300 rounded-lg px-3 py-2.5"
                  />
                </label>

                <label>
                  <span className="text-sm font-bold text-raf-navy">
                    Contact
                  </span>

                  <input
                    value={
                      form.lent_to_contact
                    }
                    onChange={(
                      event
                    ) =>
                      update(
                        "lent_to_contact",
                        event.target.value
                      )
                    }
                    placeholder="Optional"
                    className="mt-2 w-full border border-slate-300 rounded-lg px-3 py-2.5"
                  />
                </label>

                <label>
                  <span className="text-sm font-bold text-raf-navy">
                    Due date
                  </span>

                  <input
                    type="date"
                    value={
                      form.due_date
                    }
                    onChange={(
                      event
                    ) =>
                      update(
                        "due_date",
                        event.target.value
                      )
                    }
                    className="mt-2 w-full border border-slate-300 rounded-lg px-3 py-2.5"
                  />
                </label>
              </div>
            </div>
          )}

          {mode === "return" &&
            matchingLoans.length >
              0 && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                <p className="font-black text-blue-950">
                  Open loans for
                  this item
                </p>

                <div className="mt-3 space-y-2">
                  {matchingLoans.map(
                    (loan) => {
                      const selected =
                        form.loan_id ===
                        loan.id;

                      return (
                        <button
                          type="button"
                          key={
                            loan.id
                          }
                          onClick={() => {
                            update(
                              "loan_id",
                              loan.id
                            );

                            update(
                              "quantity",
                              outstandingQuantity(
                                loan
                              )
                            );
                          }}
                          className={[
                            "w-full text-left rounded-lg border p-3",
                            selected
                              ? "border-raf-blue bg-white"
                              : "border-blue-200 bg-blue-50/50",
                          ].join(" ")}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div>
                              <p className="font-bold text-raf-navy">
                                {
                                  loan.lent_to_name
                                }
                              </p>

                              <p className="text-xs text-slate-500 mt-1">
                                Issued{" "}
                                {formatDate(
                                  loan.lent_at
                                )}
                              </p>
                            </div>

                            <ToneBadge tone="blue">
                              {outstandingQuantity(
                                loan
                              )}{" "}
                              outstanding
                            </ToneBadge>
                          </div>
                        </button>
                      );
                    }
                  )}
                </div>
              </div>
            )}

          <label className="block">
            <span className="text-sm font-bold text-raf-navy">
              Notes
            </span>

            <textarea
              rows={3}
              value={
                form.notes
              }
              onChange={(
                event
              ) =>
                update(
                  "notes",
                  event.target.value
                )
              }
              placeholder={
                mode === "remove"
                  ? "Reason for removal / write-off"
                  : "Optional transaction notes"
              }
              className="mt-2 w-full border border-slate-300 rounded-xl px-4 py-3 resize-y"
            />
          </label>

          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-3 flex gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />

              <p className="text-sm text-red-800">
                {error}
              </p>
            </div>
          )}

          {success && (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0" />

              <p className="text-sm text-emerald-900">
                {success}
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className={`w-full rounded-xl py-3.5 px-5 font-black flex items-center justify-center gap-2 disabled:opacity-50 ${
              TRANSACTION_MODES[
                mode
              ].buttonClass
            }`}
          >
            {busy ? (
              <Loader2
                size={19}
                className="animate-spin"
              />
            ) : (
              <ModeIcon
                size={19}
              />
            )}

            {
              TRANSACTION_MODES[
                mode
              ].label
            }
          </button>
        </div>
      </form>
    </div>
  );
}

function StockTable({
  items,
  search,
  setSearch,
  category,
  setCategory,
  categories,
  onTransaction,
}) {
  const filtered =
    useMemo(() => {
      const query =
        search
          .trim()
          .toLowerCase();

      return items.filter(
        (item) => {
          if (
            category &&
            item.category !==
              category
          ) {
            return false;
          }

          if (!query) {
            return true;
          }

          const haystack = [
            item.name,
            item.qr_code,
            item.category,
            item.size,
            item.notes,
          ]
            .join(" ")
            .toLowerCase();

          return haystack.includes(
            query
          );
        }
      );
    }, [
      category,
      items,
      search,
    ]);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

            <input
              type="search"
              value={search}
              onChange={(
                event
              ) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Search item, code, category, size or notes..."
              className="w-full border border-slate-300 rounded-lg pl-10 pr-4 py-2.5 outline-none focus:border-raf-blue"
            />
          </div>

          <select
            value={category}
            onChange={(
              event
            ) =>
              setCategory(
                event.target.value
              )
            }
            className="border border-slate-300 bg-white rounded-lg px-3 py-2.5 min-w-56"
          >
            <option value="">
              All categories
            </option>

            {categories.map(
              (entry) => (
                <option
                  key={entry}
                  value={entry}
                >
                  {entry}
                </option>
              )
            )}
          </select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {filtered.length ===
        0 ? (
          <div className="py-16 px-5 text-center">
            <Package className="w-12 h-12 text-slate-300 mx-auto" />

            <h3 className="font-black text-raf-navy mt-3">
              No stock found
            </h3>

            <p className="text-sm text-slate-500 mt-1">
              Change the search
              filters or add an
              inventory item.
            </p>
          </div>
        ) : (
          <>
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-slate-500">
                      Item
                    </th>

                    <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-slate-500">
                      Category
                    </th>

                    <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-slate-500">
                      QR / Asset
                    </th>

                    <th className="text-center px-5 py-3 text-xs uppercase tracking-wider text-slate-500">
                      Available
                    </th>

                    <th className="text-center px-5 py-3 text-xs uppercase tracking-wider text-slate-500">
                      Total
                    </th>

                    <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-slate-500">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {filtered.map(
                    (item) => {
                      const total =
                        Number(
                          item.quantity_total ||
                            0
                        );

                      const available =
                        Number(
                          item.quantity_available ||
                            0
                        );

                      const issued =
                        Math.max(
                          0,
                          total -
                            available
                        );

                      return (
                        <tr
                          key={
                            item.id
                          }
                          className="hover:bg-slate-50"
                        >
                          <td className="px-5 py-4">
                            <p className="font-black text-raf-navy">
                              {
                                item.name
                              }
                            </p>

                            <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
                              {item.size && (
                                <span>
                                  Size:{" "}
                                  {
                                    item.size
                                  }
                                </span>
                              )}

                              {issued >
                                0 && (
                                <ToneBadge tone="blue">
                                  {
                                    issued
                                  }{" "}
                                  issued
                                </ToneBadge>
                              )}
                            </div>

                            {item.notes && (
                              <p className="text-xs text-slate-500 mt-2 max-w-sm">
                                {
                                  item.notes
                                }
                              </p>
                            )}
                          </td>

                          <td className="px-5 py-4 text-slate-600">
                            {
                              item.category
                            }
                          </td>

                          <td className="px-5 py-4">
                            <code className="text-xs bg-slate-100 rounded px-2 py-1.5">
                              {
                                item.qr_code
                              }
                            </code>
                          </td>

                          <td className="px-5 py-4 text-center">
                            <span
                              className={[
                                "font-black text-lg",
                                available ===
                                0
                                  ? "text-red-700"
                                  : "text-emerald-700",
                              ].join(" ")}
                            >
                              {
                                available
                              }
                            </span>
                          </td>

                          <td className="px-5 py-4 text-center font-bold text-slate-700">
                            {total}
                          </td>

                          <td className="px-5 py-4">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  onTransaction(
                                    "add",
                                    item
                                  )
                                }
                                className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                                title="Add stock"
                              >
                                <PackagePlus
                                  size={
                                    16
                                  }
                                />
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  onTransaction(
                                    "lend",
                                    item
                                  )
                                }
                                disabled={
                                  available <
                                  1
                                }
                                className="inline-flex items-center gap-1.5 bg-raf-navy text-white rounded-lg px-3 py-2 font-semibold disabled:opacity-40"
                              >
                                <ArrowUpFromLine
                                  size={
                                    15
                                  }
                                />
                                Issue
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>

            <div className="lg:hidden divide-y divide-slate-100">
              {filtered.map(
                (item) => {
                  const total =
                    Number(
                      item.quantity_total ||
                        0
                    );

                  const available =
                    Number(
                      item.quantity_available ||
                        0
                    );

                  return (
                    <div
                      key={
                        item.id
                      }
                      className="p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-black text-raf-navy text-lg">
                            {
                              item.name
                            }
                          </p>

                          <p className="text-sm text-slate-500 mt-1">
                            {
                              item.category
                            }
                            {item.size
                              ? ` • ${item.size}`
                              : ""}
                          </p>

                          <code className="mt-2 inline-block text-xs bg-slate-100 rounded px-2 py-1">
                            {
                              item.qr_code
                            }
                          </code>
                        </div>

                        <div className="text-right shrink-0">
                          <p className="text-2xl font-black text-raf-navy">
                            {
                              available
                            }
                          </p>

                          <p className="text-[10px] uppercase tracking-wider text-slate-400">
                            available
                          </p>

                          <p className="text-xs text-slate-500 mt-1">
                            {total} total
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            onTransaction(
                              "add",
                              item
                            )
                          }
                          className="inline-flex items-center justify-center gap-2 border border-slate-300 rounded-lg px-3 py-2.5 font-semibold text-raf-navy"
                        >
                          <PackagePlus
                            size={16}
                          />
                          Add Stock
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            onTransaction(
                              "lend",
                              item
                            )
                          }
                          disabled={
                            available <
                            1
                          }
                          className="inline-flex items-center justify-center gap-2 bg-raf-navy text-white rounded-lg px-3 py-2.5 font-semibold disabled:opacity-40"
                        >
                          <ArrowUpFromLine
                            size={16}
                          />
                          Issue
                        </button>
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function LoansPanel({
  loans,
  onReturn,
}) {
  const sorted =
    [...loans].sort(
      (a, b) => {
        const aOverdue =
          isOverdue(a)
            ? 1
            : 0;

        const bOverdue =
          isOverdue(b)
            ? 1
            : 0;

        if (
          aOverdue !==
          bOverdue
        ) {
          return (
            bOverdue -
            aOverdue
          );
        }

        return String(
          b.lent_at || ""
        ).localeCompare(
          String(
            a.lent_at || ""
          )
        );
      }
    );

  if (!sorted.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl py-16 px-5 text-center">
        <PackageCheck className="w-12 h-12 text-emerald-300 mx-auto" />

        <h3 className="font-black text-raf-navy mt-3">
          Nothing currently
          issued
        </h3>

        <p className="text-sm text-slate-500 mt-1">
          Open loans will appear
          here.
        </p>
      </div>
    );
  }

  return (
    <div className="grid xl:grid-cols-2 gap-4">
      {sorted.map(
        (loan) => {
          const overdue =
            isOverdue(loan);

          const outstanding =
            outstandingQuantity(
              loan
            );

          return (
            <div
              key={loan.id}
              className={[
                "bg-white border rounded-xl overflow-hidden",
                overdue
                  ? "border-red-300"
                  : "border-slate-200",
              ].join(" ")}
            >
              <div
                className={[
                  "px-5 py-3 flex items-center justify-between gap-3 border-b",
                  overdue
                    ? "bg-red-50 border-red-200"
                    : "bg-slate-50 border-slate-200",
                ].join(" ")}
              >
                <div className="flex items-center gap-2">
                  <UserRound
                    size={
                      17
                    }
                    className={
                      overdue
                        ? "text-red-700"
                        : "text-raf-navy"
                    }
                  />

                  <p className="font-black text-raf-navy">
                    {
                      loan.lent_to_name
                    }
                  </p>
                </div>

                {overdue ? (
                  <ToneBadge tone="red">
                    Overdue
                  </ToneBadge>
                ) : (
                  <ToneBadge tone="blue">
                    Open loan
                  </ToneBadge>
                )}
              </div>

              <div className="p-5">
                <h3 className="text-xl font-black text-raf-navy">
                  {
                    loan.item_name
                  }
                </h3>

                <p className="text-sm text-slate-500 mt-1">
                  {
                    loan.category
                  }
                </p>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                      Outstanding
                    </p>

                    <p className="text-xl font-black text-raf-navy mt-1">
                      {
                        outstanding
                      }
                    </p>
                  </div>

                  <div
                    className={[
                      "rounded-lg p-3",
                      overdue
                        ? "bg-red-50"
                        : "bg-slate-50",
                    ].join(" ")}
                  >
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                      Due
                    </p>

                    <p
                      className={[
                        "font-black mt-1",
                        overdue
                          ? "text-red-800"
                          : "text-raf-navy",
                      ].join(" ")}
                    >
                      {formatDateOnly(
                        loan.due_date
                      )}
                    </p>
                  </div>
                </div>

                <div className="mt-4 text-sm text-slate-600 space-y-1">
                  <p>
                    Issued{" "}
                    {formatDate(
                      loan.lent_at
                    )}
                  </p>

                  {loan.lent_to_contact && (
                    <p>
                      Contact:{" "}
                      {
                        loan.lent_to_contact
                      }
                    </p>
                  )}

                  {loan.notes && (
                    <p className="text-slate-500">
                      {
                        loan.notes
                      }
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() =>
                    onReturn(
                      loan
                    )
                  }
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-blue-700 text-white rounded-lg px-4 py-2.5 font-bold"
                >
                  <RotateCcw
                    size={17}
                  />
                  Record Return
                </button>
              </div>
            </div>
          );
        }
      )}
    </div>
  );
}

function HistoryPanel({
  history,
}) {
  if (!history.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl py-16 px-5 text-center">
        <History className="w-12 h-12 text-slate-300 mx-auto" />

        <h3 className="font-black text-raf-navy mt-3">
          No inventory history
          yet
        </h3>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="divide-y divide-slate-100">
        {history.map(
          (entry) => (
            <div
              key={entry.id}
              className="p-4 md:p-5"
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">
                    <ClipboardList
                      size={
                        17
                      }
                      className="text-raf-navy"
                    />
                  </div>

                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-black text-raf-navy">
                        {
                          entry.item_name
                        }
                      </p>

                      <ToneBadge
                        tone={actionTone(
                          entry.action
                        )}
                      >
                        {actionLabel(
                          entry.action
                        )}
                      </ToneBadge>
                    </div>

                    <p className="text-sm text-slate-600 mt-1">
                      Quantity:{" "}
                      <strong>
                        {
                          entry.quantity
                        }
                      </strong>
                      {" • "}
                      {
                        entry.category
                      }
                    </p>

                    {entry.lent_to_name && (
                      <p className="text-sm text-slate-600 mt-1">
                        Issued to:{" "}
                        <strong>
                          {
                            entry.lent_to_name
                          }
                        </strong>
                      </p>
                    )}

                    {entry.notes && (
                      <p className="text-sm text-slate-500 mt-2 whitespace-pre-wrap">
                        {
                          entry.notes
                        }
                      </p>
                    )}
                  </div>
                </div>

                <div className="text-left md:text-right text-xs text-slate-500 shrink-0">
                  <p>
                    {formatDate(
                      entry.created_at
                    )}
                  </p>

                  <p className="mt-1">
                    {
                      entry.performed_by_name
                    }
                  </p>

                  <code className="inline-block mt-1 bg-slate-100 rounded px-1.5 py-0.5">
                    {
                      entry.qr_code
                    }
                  </code>
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}

function ImportPanel({
  onImported,
}) {
  const [file, setFile] =
    useState(null);

  const [busy, setBusy] =
    useState(false);

  const [error, setError] =
    useState("");

  const [result, setResult] =
    useState(null);

  const submit = async (
    event
  ) => {
    event.preventDefault();

    setError("");
    setResult(null);

    if (!file) {
      setError(
        "Choose an Excel or Word file first."
      );
      return;
    }

    const body =
      new FormData();

    body.append(
      "file",
      file
    );

    setBusy(true);

    try {
      const { data } =
        await api.post(
          "/inventory/import",
          body,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",
            },
          }
        );

      setResult(data);

      await onImported();
    } catch (err) {
      setError(
        err?.response?.data
          ?.detail ||
          "Unable to import the inventory file."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid xl:grid-cols-[1fr_420px] gap-5">
      <form
        onSubmit={submit}
        className="bg-white border border-slate-200 rounded-xl p-5 md:p-6"
      >
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
            <FileSpreadsheet
              size={22}
            />
          </div>

          <div>
            <h2 className="font-black text-xl text-raf-navy">
              Bulk Inventory
              Import
            </h2>

            <p className="text-sm text-slate-500 mt-1">
              Import existing
              stores records from
              Excel or a Word table.
            </p>
          </div>
        </div>

        <label className="block mt-5">
          <span className="text-sm font-bold text-raf-navy">
            Inventory file
          </span>

          <input
            type="file"
            accept=".xlsx,.xls,.docx"
            onChange={(
              event
            ) =>
              setFile(
                event.target
                  .files?.[0] ||
                  null
              )
            }
            className="mt-2 block w-full border border-slate-300 rounded-xl p-3"
          />
        </label>

        <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4">
          <p className="font-bold text-raf-navy">
            Expected columns
          </p>

          <div className="mt-2 flex flex-wrap gap-2">
            {[
              "qr_code",
              "name",
              "category",
              "size",
              "quantity",
              "notes",
            ].map(
              (column) => (
                <code
                  key={
                    column
                  }
                  className="bg-white border border-slate-200 rounded px-2 py-1 text-xs"
                >
                  {column}
                </code>
              )
            )}
          </div>

          <p className="text-xs text-slate-500 mt-3">
            Existing QR codes
            increase the existing
            stock quantity rather
            than creating duplicate
            items.
          </p>
        </div>

        {error && (
          <div className="mt-4 rounded-xl bg-red-50 border border-red-200 p-3 flex gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />

            <p className="text-sm text-red-800">
              {error}
            </p>
          </div>
        )}

        {result && (
          <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 p-4">
            <p className="font-bold text-emerald-900">
              Import complete
            </p>

            <p className="text-sm text-emerald-800 mt-2">
              {result.created}{" "}
              created •{" "}
              {result.updated}{" "}
              updated •{" "}
              {result.skipped}{" "}
              skipped
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={
            busy || !file
          }
          className="mt-5 inline-flex items-center justify-center gap-2 bg-raf-navy text-white rounded-lg px-5 py-3 font-bold disabled:opacity-50"
        >
          {busy ? (
            <Loader2
              size={18}
              className="animate-spin"
            />
          ) : (
            <Upload
              size={18}
            />
          )}

          Import Inventory
        </button>
      </form>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
        <Archive className="w-8 h-8 text-blue-800" />

        <h3 className="font-black text-blue-950 mt-3">
          Migration tip
        </h3>

        <p className="text-sm text-blue-900 mt-2 leading-relaxed">
          If your current stores
          record does not already
          contain QR codes, add a
          unique asset code to each
          row before importing.
          The code can later be
          printed as a QR / barcode
          label.
        </p>

        <p className="text-sm text-blue-900 mt-3 leading-relaxed">
          For grouped stock such as
          identical shirts of one
          size, one code can
          represent the whole
          quantity. Individually
          controlled assets can use
          one code per item.
        </p>
      </div>
    </div>
  );
}

export const InventoryPanel =
  () => {
    const [activeTab, setActiveTab] =
      useState("stock");

    const [items, setItems] =
      useState([]);

    const [loans, setLoans] =
      useState([]);

    const [history, setHistory] =
      useState([]);

    const [categories, setCategories] =
      useState(
        DEFAULT_CATEGORIES
      );

    const [loading, setLoading] =
      useState(true);

    const [error, setError] =
      useState("");

    const [search, setSearch] =
      useState("");

    const [category, setCategory] =
      useState("");

    const [showNewItem, setShowNewItem] =
      useState(false);

    const [
      transactionPreset,
      setTransactionPreset,
    ] = useState({
      mode: "lend",
      code: "",
      loan: null,
    });

    const loadAll =
      useCallback(
        async () => {
          setLoading(true);
          setError("");

          try {
            const [
              itemsResponse,
              loansResponse,
              historyResponse,
              categoryResponse,
            ] =
              await Promise.all([
                api.get(
                  "/inventory/items"
                ),
                api.get(
                  "/inventory/loans"
                ),
                api.get(
                  "/inventory/history?limit=300"
                ),
                api.get(
                  "/inventory/categories"
                ),
              ]);

            setItems(
              Array.isArray(
                itemsResponse.data
              )
                ? itemsResponse.data
                : []
            );

            setLoans(
              Array.isArray(
                loansResponse.data
              )
                ? loansResponse.data
                : []
            );

            setHistory(
              Array.isArray(
                historyResponse.data
              )
                ? historyResponse.data
                : []
            );

            if (
              Array.isArray(
                categoryResponse.data
              ) &&
              categoryResponse.data
                .length
            ) {
              setCategories(
                categoryResponse.data
              );
            }
          } catch (err) {
            setError(
              err?.response?.data
                ?.detail ||
                "Unable to load the stores inventory."
            );
          } finally {
            setLoading(false);
          }
        },
        []
      );

    useEffect(() => {
      loadAll();
    }, [loadAll]);

    const stats =
      useMemo(() => {
        const totalUnits =
          items.reduce(
            (
              sum,
              item
            ) =>
              sum +
              Number(
                item.quantity_total ||
                  0
              ),
            0
          );

        const availableUnits =
          items.reduce(
            (
              sum,
              item
            ) =>
              sum +
              Number(
                item.quantity_available ||
                  0
              ),
            0
          );

        const issuedUnits =
          Math.max(
            0,
            totalUnits -
              availableUnits
          );

        const overdueLoans =
          loans.filter(
            isOverdue
          ).length;

        return {
          lines:
            items.length,
          totalUnits,
          availableUnits,
          issuedUnits,
          overdueLoans,
        };
      }, [
        items,
        loans,
      ]);

    const startTransaction =
      (
        mode,
        item = null
      ) => {
        setTransactionPreset({
          mode,
          code:
            item?.qr_code ||
            "",
          loan: null,
        });

        setActiveTab(
          "transactions"
        );
      };

    const startReturn =
      (loan) => {
        setTransactionPreset({
          mode: "return",
          code:
            loan.qr_code,
          loan,
        });

        setActiveTab(
          "transactions"
        );
      };

    if (
      loading &&
      items.length === 0
    ) {
      return (
        <div className="py-20 flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-raf-navy" />

          <p className="text-slate-500">
            Loading stores...
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-5">
        <div className="bg-raf-navy text-white rounded-xl overflow-hidden border-b-4 border-raf-red">
          <div className="p-5 md:p-6">
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                  <Boxes
                    size={25}
                  />
                </div>

                <div>
                  <p className="text-xs uppercase tracking-widest text-white/55 font-bold">
                    Stores
                  </p>

                  <h1 className="text-2xl md:text-3xl font-black">
                    Squadron
                    Inventory
                  </h1>

                  <p className="text-sm text-white/65 mt-1">
                    Stock control,
                    issue, return
                    and audit
                    history.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setShowNewItem(
                      true
                    )
                  }
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-white text-raf-navy px-4 py-2.5 font-black"
                >
                  <PackagePlus
                    size={18}
                  />
                  New Item
                </button>

                <button
                  type="button"
                  onClick={
                    loadAll
                  }
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/30 px-4 py-2.5 font-semibold"
                >
                  <RefreshCw
                    size={17}
                  />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-3">
          <StatCard
            icon={Tag}
            label="Stock lines"
            value={
              stats.lines
            }
            note="Distinct inventory records"
          />

          <StatCard
            icon={Package}
            label="Total units"
            value={
              stats.totalUnits
            }
            note="Including issued items"
          />

          <StatCard
            icon={PackageCheck}
            label="Available"
            value={
              stats.availableUnits
            }
            tone="green"
          />

          <StatCard
            icon={ArrowUpFromLine}
            label="Issued"
            value={
              stats.issuedUnits
            }
            tone="amber"
          />

          <StatCard
            icon={Clock3}
            label="Overdue"
            value={
              stats.overdueLoans
            }
            note="Open loans past due date"
            tone={
              stats.overdueLoans
                ? "red"
                : "green"
            }
          />
        </div>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />

            <p className="text-sm text-red-800">
              {error}
            </p>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl p-2 overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {TABS.map(
              (tab) => {
                const Icon =
                  tab.icon;

                const active =
                  activeTab ===
                  tab.key;

                const badge =
                  tab.key ===
                  "loans"
                    ? loans.length
                    : null;

                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() =>
                      setActiveTab(
                        tab.key
                      )
                    }
                    className={[
                      "inline-flex items-center gap-2 rounded-lg px-4 py-2.5 font-semibold transition",
                      active
                        ? "bg-raf-navy text-white"
                        : "text-slate-600 hover:bg-slate-50",
                    ].join(" ")}
                  >
                    <Icon
                      size={17}
                    />

                    {tab.label}

                    {badge !==
                      null &&
                      badge > 0 && (
                        <span
                          className={[
                            "rounded-full px-1.5 py-0.5 text-[10px] font-black",
                            active
                              ? "bg-white text-raf-navy"
                              : "bg-amber-100 text-amber-900",
                          ].join(" ")}
                        >
                          {badge}
                        </span>
                      )}
                  </button>
                );
              }
            )}
          </div>
        </div>

        {activeTab ===
          "stock" && (
          <StockTable
            items={items}
            search={search}
            setSearch={
              setSearch
            }
            category={
              category
            }
            setCategory={
              setCategory
            }
            categories={
              categories
            }
            onTransaction={
              startTransaction
            }
          />
        )}

        {activeTab ===
          "transactions" && (
          <TransactionPanel
            key={`${transactionPreset.mode}-${transactionPreset.code}-${transactionPreset.loan?.id || ""}`}
            items={items}
            categories={
              categories
            }
            loans={loans}
            initialMode={
              transactionPreset.mode
            }
            initialCode={
              transactionPreset.code
            }
            initialLoan={
              transactionPreset.loan
            }
            onComplete={
              loadAll
            }
          />
        )}

        {activeTab ===
          "loans" && (
          <LoansPanel
            loans={loans}
            onReturn={
              startReturn
            }
          />
        )}

        {activeTab ===
          "history" && (
          <HistoryPanel
            history={
              history
            }
          />
        )}

        {activeTab ===
          "import" && (
          <ImportPanel
            onImported={
              loadAll
            }
          />
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex gap-3">
            <Shirt className="w-5 h-5 text-blue-800 shrink-0 mt-0.5" />

            <div>
              <p className="font-bold text-blue-950">
                Uniform and
                equipment can use
                the same inventory.
              </p>

              <p className="text-sm text-blue-900 mt-1">
                Group identical
                items under one QR
                code where quantity
                control is enough,
                or use individual
                codes for assets
                that need to be
                tracked separately.
                For cadet loans,
                service numbers can
                be used as the issue
                reference instead
                of names.
              </p>
            </div>
          </div>
        </div>

        {showNewItem && (
          <NewItemModal
            categories={
              categories
            }
            onClose={() =>
              setShowNewItem(
                false
              )
            }
            onCreated={async () => {
              setShowNewItem(
                false
              );

              await loadAll();
            }}
          />
        )}
      </div>
    );
  };

export default InventoryPanel;